/**
 * Water care quiz (sections/water-care-quiz.liquid).
 * The section's theme-editor blocks describe a decision tree: each question block is followed by
 * its answer blocks, and each answer points to the next question or to a results page.
 * The functions below build, check and walk that tree; tools/water-care-quiz.test.mjs tests them.
 */
(() => {
  const question = (id, heading) => ({ type: 'question', id, heading });
  const answer = (title, slug, image, next, result) => ({ type: 'answer', title, slug, image, next, result });

  // Used when the editor's blocks don't form a valid tree. Keep in step with
  // templates/page.water-care-quiz.json.
  const DEFAULT_BLOCKS = [
    question('q1', 'Do you have a Bullfrog Spa?'),
    answer('Yes', 'yes', 'water-care-quiz-bullfrog.png', 'q2', ''),
    answer('No', 'no', '', '', '/pages/quiz-landing-floating-ball'),
    question('q2', 'Hot tub or swim spa?'),
    answer('Hot Tub', 'hot-tub', 'water-care-quiz-hot-tub.png', 'q3', ''),
    answer('Swim Spa', 'swim-spa', 'water-care-quiz-swim-spa.png', '', '/pages/quiz-landing-frog-swim'),
    question('q3', 'What do your filters look like?'),
    answer('Flat', 'flat', 'water-care-quiz-filter-flat.png', 'q4', ''),
    answer('Two Round', 'two-round', 'water-care-quiz-filter-two-round.png', 'q6', ''),
    answer('One Round', 'one-round', 'water-care-quiz-filter-one-round.png', '', '/pages/quiz-landing-floating-ball'),
    question('q4', 'What does your filter holder look like?'),
    answer('Cartridge Slots', 'cartridge-slots', 'water-care-quiz-holder-slots.png', 'q5', ''),
    answer('No Slots', 'no-slots', 'water-care-quiz-holder-no-slots.png', '', '/pages/quiz-landing-simplicity-classic-plus'),
    question('q5', 'How big is your spa?'),
    answer('Small (1-2 people)', 'small', '', 'q7', ''),
    answer('Medium (3-4 people)', 'medium', '', 'q7', ''),
    answer('Large (5+ people)', 'large', '', '', '/pages/quiz-landing-simplicity-xl'),
    question('q6', 'Do you have an @ease cartridge holder?'),
    answer('Yes', 'holder-yes', 'water-care-quiz-ease-holder.png', '', '/pages/quiz-lander-frog-classic'),
    answer('No', 'holder-no', '', '', '/pages/quiz-lander-frog-classic'),
    question('q7', 'How often do you use your spa?'),
    answer('Regular Use', 'regular-use', '', '', '/pages/quiz-landing-simplicity'),
    answer('Heavy Use', 'heavy-use', '', '', '/pages/quiz-landing-simplicity-xl'),
  ];

  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  const clean = (value) => String(value || '').trim();

  // An answer with a results page ends the quiz there, even if it also names a next question.
  const nextQuestionOf = (answer) => (answer.result ? '' : answer.next);

  function findLoop(questions) {
    const finished = new Set();
    const visit = (id, onPath) => {
      if (onPath.has(id)) return id;
      if (finished.has(id) || !questions[id]) return null;
      onPath.add(id);
      for (const answer of questions[id].answers) {
        const loop = nextQuestionOf(answer) && visit(nextQuestionOf(answer), onPath);
        if (loop) return loop;
      }
      onPath.delete(id);
      finished.add(id);
      return null;
    };
    for (const id of Object.keys(questions)) {
      const loop = visit(id, new Set());
      if (loop) return loop;
    }
    return null;
  }

  function buildTree(blocks) {
    const errors = [];
    const questions = {};
    let start = '';
    let current = null;

    for (const block of blocks || []) {
      if (block.type === 'question') {
        const id = clean(block.id);
        current = { id, heading: block.heading || '', answers: [] };
        if (!id) {
          errors.push('Question block is missing its ID');
        } else if (questions[id]) {
          errors.push(`Duplicate question ID "${id}"`);
        } else {
          questions[id] = current;
          if (!start) start = id;
        }
      } else if (block.type === 'answer') {
        const title = block.title || '';
        if (!current) {
          errors.push(`Answer "${title}" comes before any question`);
          continue;
        }
        current.answers.push({
          title,
          slug: clean(block.slug) || slugify(title),
          image: clean(block.image),
          next: clean(block.next),
          result: clean(block.result),
        });
      }
    }

    if (!start) errors.push('No questions');
    for (const { id, answers } of Object.values(questions)) {
      if (!answers.length) errors.push(`Question "${id}" has no answers`);
      for (const answer of answers) {
        if (!answer.result && !answer.next) {
          errors.push(`Answer "${answer.title}" on question "${id}" has no next question or results page`);
        } else if (nextQuestionOf(answer) && !questions[answer.next]) {
          errors.push(`Answer "${answer.title}" on question "${id}" points to unknown question "${answer.next}"`);
        }
      }
    }
    const loop = findLoop(questions);
    if (loop) errors.push(`Question "${loop}" can be reached again from itself`);

    return { tree: { start, questions }, errors };
  }

  function pickTree(blocks) {
    const built = buildTree(blocks);
    if (!built.errors.length) return { ...built, fallback: false };
    return { tree: buildTree(DEFAULT_BLOCKS).tree, errors: built.errors, fallback: true };
  }

  // Number of questions on the longest path. Only call on a tree without errors.
  function longestPath(tree) {
    const depth = (id) =>
      1 + Math.max(0, ...tree.questions[id].answers.filter(nextQuestionOf).map((answer) => depth(answer.next)));
    return tree.start ? depth(tree.start) : 0;
  }

  function progressPercent(depth, maxDepth) {
    return maxDepth ? Math.round((depth / maxDepth) * 100) : 0;
  }

  function startState(tree) {
    return { answers: [], questions: [tree.start] };
  }

  const currentQuestion = (tree, state) => tree.questions[state.questions[state.questions.length - 1]];
  const slugsOf = (tree, state) =>
    state.answers.map((index, step) => tree.questions[state.questions[step]].answers[index].slug);

  // Returns null for an answer index the current question doesn't have.
  function choose(tree, state, index) {
    const answer = currentQuestion(tree, state)?.answers[index];
    if (!answer) return null;
    if (nextQuestionOf(answer) === '') {
      return { done: true, destination: answer.result, slugs: [...slugsOf(tree, state), answer.slug] };
    }
    return { done: false, state: { answers: [...state.answers, index], questions: [...state.questions, answer.next] } };
  }

  function goBack(state) {
    if (!state.answers.length) return null;
    return { answers: state.answers.slice(0, -1), questions: state.questions.slice(0, -1) };
  }

  function replay(tree, answers) {
    if (!Array.isArray(answers)) return null;
    let state = startState(tree);
    for (const index of answers) {
      const outcome = choose(tree, state, index);
      if (!outcome || outcome.done) return null;
      state = outcome.state;
    }
    return state;
  }

  function resultUrl(destination, slugs, options = {}) {
    const relative = !/^([a-z][a-z0-9+.-]*:)?\/\//i.test(destination);
    const url = new URL(destination, 'https://relative.invalid');
    url.searchParams.set('quiz', 'water-care');
    url.searchParams.set('answers', slugs.join('-'));
    if (options.test) url.searchParams.set('quiz_test', '1');
    return relative ? url.pathname + url.search + url.hash : url.href;
  }

  // Theme asset file names (no slash) resolve against the assets folder; URLs pass through.
  function imageUrl(value, assetBase) {
    if (!value) return '';
    return value.includes('/') ? value : assetBase + value;
  }

  // Sends a quiz event to whichever analytics tools are on the page. Google loads through the
  // Google & YouTube app's sandboxed pixel, so it only sees the page views (with ?quiz=…&answers=…).
  // Test runs (quiz_test=1) send nothing. A tool that throws must never stop the quiz.
  function track(name, detail, env) {
    if (env.test) return;
    const safely = (send) => {
      try {
        send();
      } catch (error) {
        // Ignore: the shopper still gets the next question or their results page.
      }
    };
    if (env.dataLayer) safely(() => env.dataLayer.push({ event: name, ...detail }));
    if (env.publish) safely(() => env.publish(name, detail));
    if (!env.clarity) return;
    safely(() => {
      env.clarity('event', name);
      if (name === 'quiz_answer') env.clarity('set', `quiz_${detail.question}`, detail.answer);
      if (name === 'quiz_complete') env.clarity('set', 'quiz_result', detail.result);
    });
  }

  const api = {
    DEFAULT_BLOCKS,
    slugify,
    buildTree,
    pickTree,
    longestPath,
    progressPercent,
    startState,
    choose,
    goBack,
    replay,
    resultUrl,
    imageUrl,
    track,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  window.WaterCareQuiz = api;

  const QUIZ = 'water-care';
  const ADVANCE_DELAY = 300;

  // The quiz screens. Questions are history entries ({ wcq: 'question', answers }), so the
  // browser's Back button and the quiz's own Back button both step back one question.
  class WaterCareQuizElement extends HTMLElement {
    connectedCallback() {
      if (this.tree) return;

      let blocks = [];
      try {
        blocks = JSON.parse(this.querySelector('[data-wcq-blocks]')?.textContent || '[]');
      } catch (error) {
        blocks = [];
      }
      const { tree, errors } = pickTree(blocks);
      this.tree = tree;
      this.maxDepth = longestPath(tree);
      this.assetBase = this.dataset.assetBase || '';
      this.test = new URLSearchParams(window.location.search).get('quiz_test') === '1';
      this.busy = false;
      this.state = null;

      this.intro = this.querySelector('[data-wcq-intro]');
      this.card = this.querySelector('[data-wcq-card]');
      this.percent = this.querySelector('[data-wcq-percent]');
      this.progress = this.querySelector('[data-wcq-progress] > span');
      this.live = this.querySelector('[data-wcq-live]');
      this.question = this.querySelector('[data-wcq-question]');
      this.answers = this.querySelector('[data-wcq-answers]');

      if (this.dataset.designMode === 'true' && errors.length) this.showErrors(errors);

      this.querySelector('[data-wcq-start]').addEventListener('click', () => this.start());
      this.querySelector('[data-wcq-back]').addEventListener('click', () => window.history.back());
      this.answers.addEventListener('click', (event) => {
        // The second click of a double-click can land on the next question's answer; ignore it.
        // Keyboard "clicks" have detail 0.
        if (event.detail > 1) return;
        const button = event.target.closest('.wcq__answer');
        if (button) this.pick(Number(button.dataset.index), button);
      });
      window.addEventListener('popstate', (event) => this.restore(event.state));
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.busy = false;
        this.answers.querySelectorAll('.is-selected').forEach((button) => button.classList.remove('is-selected'));
      });

      if (!this.restore(window.history.state)) window.history.replaceState({ wcq: 'intro' }, '');
    }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent('water-care-quiz:event', { bubbles: true, detail: { name, ...detail } }));
      const analytics = window.Shopify?.analytics;
      track(name, detail, {
        test: this.test,
        clarity: window.clarity,
        publish: analytics?.publish ? analytics.publish.bind(analytics) : undefined,
        dataLayer: window.dataLayer,
      });
    }

    start() {
      const state = startState(this.tree);
      window.history.pushState({ wcq: 'question', answers: state.answers }, '');
      this.showQuestion(state);
      this.emit('quiz_start', { quiz: QUIZ });
    }

    // Shows the history entry's question, or the intro. Returns false when there was no question.
    restore(saved) {
      const state = saved?.wcq === 'question' ? replay(this.tree, saved.answers) : null;
      this.busy = false;
      if (state) {
        this.showQuestion(state);
        return true;
      }
      this.showIntro();
      return false;
    }

    pick(index, button) {
      if (this.busy || !this.state) return;
      this.busy = true;
      button.classList.add('is-selected');

      const state = this.state;
      const question = this.tree.questions[state.questions[state.questions.length - 1]];
      const picked = question.answers[index];
      setTimeout(() => {
        const outcome = choose(this.tree, state, index);
        if (!outcome) {
          this.busy = false;
          return;
        }
        this.emit('quiz_answer', {
          quiz: QUIZ,
          step: state.answers.length + 1,
          question: question.id,
          question_text: question.heading,
          answer: picked.slug,
          answer_text: picked.title,
        });
        if (outcome.done) {
          this.emit('quiz_complete', { quiz: QUIZ, result: outcome.destination, answers: outcome.slugs.join('-') });
          window.location.assign(resultUrl(outcome.destination, outcome.slugs, { test: this.test }));
          return;
        }
        window.history.pushState({ wcq: 'question', answers: outcome.state.answers }, '');
        this.showQuestion(outcome.state);
        this.busy = false;
      }, ADVANCE_DELAY);
    }

    showIntro() {
      this.state = null;
      this.card.hidden = true;
      this.intro.hidden = false;
    }

    showQuestion(state) {
      this.state = state;
      const question = this.tree.questions[state.questions[state.questions.length - 1]];
      const percent = progressPercent(state.answers.length, this.maxDepth);

      this.intro.hidden = true;
      this.card.hidden = false;
      this.percent.textContent = `${percent}%`;
      this.progress.style.width = `${percent}%`;
      this.live.textContent = `${percent}% complete`;
      this.question.textContent = question.heading;
      this.answers.replaceChildren(...question.answers.map((answer, index) => this.answerButton(answer, index)));

      this.card.classList.add('is-entering');
      requestAnimationFrame(() => requestAnimationFrame(() => this.card.classList.remove('is-entering')));
      this.question.focus();
    }

    answerButton(answer, index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wcq__answer';
      button.dataset.index = index;

      const label = document.createElement('span');
      label.className = 'wcq__answer-label';
      label.textContent = answer.title;
      button.append(label);

      const src = imageUrl(answer.image, this.assetBase);
      if (src) {
        const image = document.createElement('img');
        image.className = 'wcq__answer-image';
        // Wide pictures (logos, product shots) are shown whole; square photos fill the thumbnail.
        image.addEventListener('load', () => {
          image.classList.toggle('is-wide', image.naturalWidth > image.naturalHeight * 1.1);
        });
        image.src = src;
        image.alt = '';
        image.width = 80;
        image.height = 80;
        button.append(image);
      }
      return button;
    }

    // Theme editor only: tells the merchant why shoppers are seeing the built-in quiz.
    showErrors(errors) {
      const box = this.querySelector('[data-wcq-errors]');
      const heading = document.createElement('p');
      heading.textContent = "This quiz's blocks have problems, so shoppers get the built-in quiz until they're fixed:";
      const list = document.createElement('ul');
      list.append(
        ...errors.map((error) => {
          const item = document.createElement('li');
          item.textContent = error;
          return item;
        })
      );
      box.replaceChildren(heading, list);
      box.hidden = false;
    }
  }

  if (!customElements.get('water-care-quiz')) customElements.define('water-care-quiz', WaterCareQuizElement);
})();
